import { RouteComponentProps } from 'react-router-dom';
import { Props } from './index';
import React from 'react';
import { authorizeAction } from '../../helpers/CheckPermissionHelper';
import { ActionType, ModuleName } from '../../core/models/enum';
import { Button, DatePicker, message, PageHeader, Radio, Select } from 'antd';
import { translations } from '../../config/translations';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    FileExcelOutlined,
    ImportOutlined,
} from '@ant-design/icons';
import SelectMonth from '../../components/select-month';
import moment from 'moment';
import {
    CompaniesClient,
    CompanyVm,
    EmployeeExpenseEmployeeVm,
    EmployeeExpensesClient,
    FileParameter,
    ProjectColumn,
} from '../../utils/api';
import { SelectOption } from '../../core/models/SelectOption';
import { getFiltersParams, getPagingSortingParams } from '../../helpers/SearchParamsHelper';
import { defaultTablePagination } from '../../config/constants';
import { capitalizeFirstLetter } from '../../helpers/StringHelper';
import EmployeeExpensesTable from './employee-expenses-table';
import { downloadFile } from '../../helpers/FileHelper';
import { showConfirm } from '../../helpers/NotificationHelper';

interface State {
    allowCreate: boolean;
    companies: SelectOption[];
    selectedCompany?: number;
    selectedMonth: number;
    selectedYear: number;
    loading: boolean;
    employeeExpenses?: EmployeeExpenseEmployeeVm[];
    totalItems: number;
    projectColumns: ProjectColumn[];
    isExporting: boolean;
}

class EmployeeExpenses extends React.Component<Props & RouteComponentProps, State> {
    private tableRef: any = React.createRef();
    private fileInputRef = React.createRef<HTMLInputElement>();

    public constructor(props: Props & RouteComponentProps) {
        super(props);

        this.state = {
            allowCreate: authorizeAction(
                props.userProfile,
                ModuleName.EmployeeExpenses,
                ActionType.Create
            ),
            companies: [],
            selectedCompany: this.getSelectedCompanyFromUrl(),
            selectedMonth: this.getSelectedMonthFromUrl(),
            selectedYear: this.getSelectedYearFromUrl(),
            loading: false,
            employeeExpenses: [],
            totalItems: 0,
            projectColumns: [],
            isExporting: false,
        };
    }

    public componentDidMount = () => {
        this.initializeDataFromUrl();
        this.getCompanies();
    };

    public componentDidUpdate(prevProps: Props & RouteComponentProps) {
        if (this.props.location.search !== prevProps.location.search) {
            this.initializeDataFromUrl();
        }
    }

    private getSelectedMonthFromUrl = (): number => {
        const params = new URLSearchParams(this.props.location.search);
        return params.get('month')
            ? parseInt(params.get('month')!)
            : (moment().month() as number) + 1;
    };

    private getSelectedYearFromUrl = (): number => {
        const params = new URLSearchParams(this.props.location.search);
        return params.get('year') ? parseInt(params.get('year')!) : moment().year();
    };

    private initializeDataFromUrl = () => {
        const selectedCompany = this.getSelectedCompanyFromUrl();
        const selectedMonth = this.getSelectedMonthFromUrl();
        const selectedYear = this.getSelectedYearFromUrl();

        this.setState({ selectedCompany, selectedMonth, selectedYear }, () => {
            if (selectedCompany && selectedMonth && selectedYear) {
                this.getEmployeeExpenses();
            }
        });
    };

    private updateQueryParams = (companyId?: number, month?: number, year?: number) => {
        const params = new URLSearchParams(this.props.location.search);
        if (companyId) {
            params.set('companyId', companyId.toString());
        }
        if (month) {
            params.set('month', month.toString());
        }
        if (year) {
            params.set('year', year.toString());
        }
        this.props.history.push({ search: params.toString() });
    };

    private getEmployeeExpenses = async () => {
        const { selectedCompany, selectedMonth, selectedYear } = this.state;
        this.setState({ loading: true });

        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const { pageIndexParam, pageSizeParam, sortByParam, sortOrderParam } =
            getPagingSortingParams(params);
        const filters = getFiltersParams(params);

        try {
            const result = await new EmployeeExpensesClient().getPaginated(
                filters.Employee_Name?.toString(),
                selectedCompany ? selectedCompany : 1,
                pageIndexParam && parseInt(pageIndexParam) ? parseInt(pageIndexParam) : 1,
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
                sortByParam ? capitalizeFirstLetter(sortByParam) : undefined,
                sortOrderParam,
                selectedMonth,
                selectedYear,
                selectedCompany
            );

            this.setState({
                employeeExpenses: result.employeeExpenses,
                projectColumns: result.projectColumns || [],
                totalItems: result.totalItems,
                loading: false,
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    private handleSelectCompany = (value: number | undefined) => {
        this.setState({ selectedCompany: value ? value : 1 }, () => {
            this.props.history.push({
                search: new URLSearchParams({
                    companyId: value?.toString() ?? '1',
                    month: this.state.selectedMonth.toString(),
                    year: this.state.selectedYear.toString(),
                    pageIndex: '1', // Reset page index to 1 when changing company
                }).toString(),
            });
        });
    };

    private handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const { selectedCompany, selectedMonth, selectedYear } = this.state;

        // Transform File to FileParameter
        const fileParameter: FileParameter = {
            data: file,
            fileName: file.name,
        };

        try {
            await new EmployeeExpensesClient().uploadEmployeeExpense(
                selectedCompany,
                selectedMonth,
                selectedYear,
                fileParameter // Pass the transformed file
            );
            message.success(translations.employeeExpenses.importSuccess);
            this.getEmployeeExpenses(); // Refresh the table data
        } catch (error) {
            console.error(error);
            message.error(translations.employeeExpenses.importError);
        }
    };

    private handleSelectMonth = (value: number) => {
        this.setState({ selectedMonth: value }, () => {
            this.updateQueryParams(this.state.selectedCompany, value, this.state.selectedYear);
        });
    };

    private handleYearChange = (date: moment.Moment | null) => {
        const year = date ? date.year() : this.state.selectedYear;
        this.setState({ selectedYear: year }, () => {
            this.updateQueryParams(this.state.selectedCompany, this.state.selectedMonth, year);
        });
    };

    private handleDecrementMonth = () => {
        let { selectedMonth, selectedYear } = this.state;

        if (selectedMonth === 1) {
            // January -> go to December of previous year
            selectedMonth = 12;
            selectedYear -= 1;
        } else {
            selectedMonth -= 1;
        }

        this.setState({ selectedMonth, selectedYear }, () => {
            this.updateQueryParams(this.state.selectedCompany, selectedMonth, selectedYear);
        });
    };

    private handleIncrementMonth = () => {
        let { selectedMonth, selectedYear } = this.state;

        if (selectedMonth === 12) {
            // December -> go to January of next year
            selectedMonth = 1;
            selectedYear += 1;
        } else {
            selectedMonth += 1;
        }

        this.setState({ selectedMonth, selectedYear }, () => {
            this.updateQueryParams(this.state.selectedCompany, selectedMonth, selectedYear);
        });
    };

    private getSelectedCompanyFromUrl = (): number | undefined => {
        const params = new URLSearchParams(this.props.location.search);
        return params.get('companyId') ? parseInt(params.get('companyId')!) : undefined;
    };

    private handleImport = async () => {
        const { employeeExpenses } = this.state;
        if (
            employeeExpenses &&
            employeeExpenses.some(
                (exp) => exp.employee && exp.employeeExpense && exp.employeeExpense.length > 0
            )
        ) {
            showConfirm(
                () => this.fileInputRef.current?.click(),
                'Ponovni uvoz troškova će izbrisati postojeće troškove. Jeste li sigurni da želite nastaviti?',
                translations.general.import,
                true
            );
        } else {
            this.fileInputRef.current?.click();
        }
    };

    private handleExport = async () => {
        const { selectedCompany, selectedMonth, selectedYear } = this.state;
        this.setState({ isExporting: true });
        let result = await new EmployeeExpensesClient().generateXlsx(
            selectedCompany,
            selectedMonth,
            selectedYear
        );
        downloadFile(result);
        this.setState({ isExporting: false });
    };

    private getCompanies = async () => {
        const companies = (await new CompaniesClient().getAll()).map(
            (unit: CompanyVm): SelectOption => ({
                label: unit.name,
                value: unit.id,
            })
        );

        if (companies.length > 0) {
            const { selectedCompany, selectedMonth, selectedYear } = this.state;
            const companyToSelect = selectedCompany || companies[0].value;
            this.setState({ companies, selectedCompany: companyToSelect }, () => {
                this.updateQueryParams(companyToSelect, selectedMonth, selectedYear);
            });
        }
    };

    public render(): React.ReactElement {
        const {
            allowCreate,
            companies,
            selectedCompany,
            selectedMonth,
            selectedYear,
            loading,
            employeeExpenses,
            projectColumns,
            isExporting,
        } = this.state;
        const { history } = this.props;

        return (
            <>
                <PageHeader
                    title={
                        <Radio.Group value={'expenses'}>
                            <Radio.Button
                                value="salaries"
                                onClick={() =>
                                    history.push(
                                        `/salaries?companyId=${selectedCompany}&month=${selectedMonth}&year=${selectedYear}`
                                    )
                                }
                            >
                                {translations.salaries.salaries}
                            </Radio.Button>
                            <Radio.Button
                                value="payrolls"
                                onClick={() =>
                                    history.push(
                                        `/payrolls?companyId=${selectedCompany}&month=${selectedMonth}&year=${selectedYear}`
                                    )
                                }
                            >
                                {translations.payrolls.payrolls}
                            </Radio.Button>
                            <Radio.Button
                                value="expenses"
                                onClick={() =>
                                    history.push(
                                        `/employee-expenses?companyId=${selectedCompany}&month=${selectedMonth}&year=${selectedYear}`
                                    )
                                }
                            >
                                {translations.employeeExpenses.employeeExpenses}
                            </Radio.Button>
                        </Radio.Group>
                    }
                    extra={
                        allowCreate
                            ? [
                                  <Button
                                      key="1"
                                      type="primary"
                                      onClick={this.handleImport}
                                      style={{
                                          zIndex: 10,
                                          float: 'right',
                                      }}
                                      className="print"
                                  >
                                      <ImportOutlined />
                                      {translations.general.import}
                                  </Button>,
                                  <Button
                                      key="2"
                                      style={{
                                          zIndex: 10,
                                          float: 'right',
                                      }}
                                      className="print"
                                      loading={isExporting}
                                      onClick={this.handleExport}
                                  >
                                      <FileExcelOutlined />
                                      {translations.general.export}
                                  </Button>,
                              ]
                            : []
                    }
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'start',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            alignItems: 'center',
                        }}
                    >
                        <Select<number>
                            showSearch
                            placeholder={translations.employees.companyPlaceholder}
                            options={companies}
                            onSelect={this.handleSelectCompany}
                            value={selectedCompany}
                            style={{ minWidth: '150px' }}
                        />
                        <DatePicker
                            picker="year"
                            value={moment(selectedYear, 'YYYY')}
                            onChange={(date) => this.handleYearChange(date)}
                            allowClear={false}
                            style={{ width: '100px' }}
                        />
                        <SelectMonth
                            value={selectedMonth}
                            onChange={this.handleSelectMonth}
                            width="100px"
                        />
                        <div>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                type="default"
                                className="no-print"
                                onClick={this.handleDecrementMonth}
                            />
                            <Button
                                icon={<ArrowRightOutlined />}
                                type="default"
                                className="no-print"
                                onClick={this.handleIncrementMonth}
                            />
                        </div>
                    </div>
                </PageHeader>
                <input
                    type="file"
                    ref={this.fileInputRef}
                    style={{ display: 'none' }}
                    onChange={this.handleFileInputChange}
                    accept=".csv"
                />
                <EmployeeExpensesTable
                    wrappedComponentRef={this.tableRef}
                    selectedCompanyId={selectedCompany}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    employeeExpensesEmployee={employeeExpenses ? employeeExpenses : []}
                    loading={loading}
                    fetchEmployeeExpenses={this.getEmployeeExpenses}
                    totalCount={this.state.totalItems}
                    projectColumns={projectColumns}
                />
            </>
        );
    }
}

export default EmployeeExpenses;
